import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/login`, {
        email,
        password,
      });
      
      if (response.data.success) {
        // Persist JWT from response body or Authorization header
        const tokenFromBody = response.data.token;
        const tokenFromHeader = response.headers?.authorization || response.headers?.Authorization;
        const token = tokenFromBody || (tokenFromHeader?.startsWith('Bearer ') ? tokenFromHeader.substring(7) : tokenFromHeader);
        if (token) {
          localStorage.setItem('token', token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('isAuthenticated', 'true');
        return response.data.user;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/register`, userData);
      
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Registration failed. Please try again.'
      );
    }
  }
);

export const registerSeller = createAsyncThunk(
  'auth/registerSeller',
  async (sellerData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/register/seller`, sellerData);
      
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Seller registration failed. Please try again.'
      );
    }
  }
);

export const updateSellerProfile = createAsyncThunk(
  'auth/updateSellerProfile',
  async ({ userId, profileData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${userId}`, profileData);

      if (response.data.success) {
        return response.data.user;
      }
      return rejectWithValue(response.data.message || 'Failed to update profile');
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile'
      );
    }
  }
);

export const updateSellerLogo = createAsyncThunk(
  'auth/updateSellerLogo',
  async ({ userId, logoUrl }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${userId}/logo`, { logoUrl });
      
      if (response.data.success) {
        return response.data.user;
      }
      return rejectWithValue(response.data.message || 'Failed to update logo');
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update logo'
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (userId, { rejectWithValue }) => {
    try {
      await axios.post(`${API_BASE_URL}/users/logout/${userId}`);
      
      // Clear localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      return true;
    } catch (error) {
      // Even if API call fails, clear local storage
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      return true;
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/verify-email?token=${token}`);
      
      if (response.data.success) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Verification failed. Please try again.'
      );
    }
  }
);

export const sendPhoneVerification = createAsyncThunk(
  'auth/sendPhoneVerification',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/send-phone-verification/${userId}`);
      
      if (response.data.success) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send verification code.'
      );
    }
  }
);

export const verifyPhone = createAsyncThunk(
  'auth/verifyPhone',
  async ({ userId, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/verify-phone`, {
        userId,
        otp,
      });
      
      if (response.data.success) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Phone verification failed.'
      );
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ userId, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/change-password`, {
        userId,
        oldPassword,
        newPassword,
      });
      
      if (response.data.success) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Password change failed.'
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/reset-password`, {
        email,
      });

      if (response.data.success) {
        return response.data.message;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Password reset failed.'
      );
    }
  }
);

export const sendEmailOtp = createAsyncThunk(
  'auth/sendEmailOtp',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/send-email-otp`, { email });
      if (response.data.success) return response.data.message;
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP.');
    }
  }
);

export const verifyEmailOtp = createAsyncThunk(
  'auth/verifyEmailOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/verify-email-otp`, { email, otp });
      if (response.data.success) return response.data.message;
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed.');
    }
  }
);

export const sendForgotPasswordOtp = createAsyncThunk(
  'auth/sendForgotPasswordOtp',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/forgot-password/send-otp`, { email });
      if (response.data.success) return response.data.message;
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP.');
    }
  }
);

export const verifyForgotPasswordOtp = createAsyncThunk(
  'auth/verifyForgotPasswordOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/forgot-password/verify-otp`, { email, otp });
      if (response.data.success) return response.data.message;
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed.');
    }
  }
);

export const resetPasswordWithOtp = createAsyncThunk(
  'auth/resetPasswordWithOtp',
  async ({ email, otp, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/forgot-password/reset`, { email, otp, newPassword });
      if (response.data.success) return response.data.message;
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Password reset failed.');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${userId}`, userData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        // Update localStorage with updated user data
        const updatedUser = response.data.user;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      } else {
        return rejectWithValue(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile. Please try again.'
      );
    }
  }
);

export const sendDeletionOtp = createAsyncThunk(
  'auth/sendDeletionOtp',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/send-deletion-otp`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      if (response.data.success) {
        return true;
      }
      return rejectWithValue(response.data.message || 'Failed to send OTP');
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send OTP. Please try again.'
      );
    }
  }
);

export const deleteUserAccount = createAsyncThunk(
  'auth/deleteUserAccount',
  async (otp, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/delete-account`, { otp }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (response.data.success) {
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        return true;
      }

      return rejectWithValue(response.data.message || 'Failed to delete account');
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete account. Please try again.'
      );
    }
  }
);

// Get initial state from localStorage
const getInitialState = () => {
  const token = localStorage.getItem('token');
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true' || Boolean(token);
  const user = localStorage.getItem('user');
  
  // Set axios default header if token exists
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  
  return {
    user: user ? JSON.parse(user) : null,
    token,
    isAuthenticated,
    loading: false,
    error: null,
    registrationSuccess: false,
    verificationMessage: null,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearVerificationMessage: (state) => {
      state.verificationMessage = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login User
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.token = localStorage.getItem('token'); // Store token in state
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload;
      })
      
      // Register User
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.registrationSuccess = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.registrationSuccess = false;
        state.error = action.payload;
      })
      
      // Register Seller
      .addCase(registerSeller.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(registerSeller.fulfilled, (state, action) => {
        state.loading = false;
        state.registrationSuccess = true;
        state.error = null;
      })
      .addCase(registerSeller.rejected, (state, action) => {
        state.loading = false;
        state.registrationSuccess = false;
        state.error = action.payload;
      })

      // Update seller profile
      .addCase(updateSellerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSellerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        if (state.user) {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        state.error = null;
      })
      .addCase(updateSellerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update seller logo
      .addCase(updateSellerLogo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSellerLogo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        if (state.user) {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        state.error = null;
      })
      .addCase(updateSellerLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Logout User
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
        state.loading = false;
      })
      .addCase(deleteUserAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationMessage = action.payload;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Send Phone Verification
      .addCase(sendPhoneVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendPhoneVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationMessage = action.payload;
        state.error = null;
      })
      .addCase(sendPhoneVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Verify Phone
      .addCase(verifyPhone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPhone.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationMessage = action.payload;
        if (state.user) {
          state.user.phoneVerified = true;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        state.error = null;
      })
      .addCase(verifyPhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationMessage = action.payload;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationMessage = action.payload;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        if (action.payload) {
          localStorage.setItem('user', JSON.stringify(action.payload));
        }
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send Email OTP
      .addCase(sendEmailOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(sendEmailOtp.fulfilled, (state, action) => { state.loading = false; state.verificationMessage = action.payload; state.error = null; })
      .addCase(sendEmailOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Verify Email OTP
      .addCase(verifyEmailOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(verifyEmailOtp.fulfilled, (state, action) => { state.loading = false; state.verificationMessage = action.payload; state.error = null; })
      .addCase(verifyEmailOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Send Forgot Password OTP
      .addCase(sendForgotPasswordOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(sendForgotPasswordOtp.fulfilled, (state, action) => { state.loading = false; state.verificationMessage = action.payload; state.error = null; })
      .addCase(sendForgotPasswordOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Verify Forgot Password OTP
      .addCase(verifyForgotPasswordOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(verifyForgotPasswordOtp.fulfilled, (state, action) => { state.loading = false; state.verificationMessage = action.payload; state.error = null; })
      .addCase(verifyForgotPasswordOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Reset Password with OTP
      .addCase(resetPasswordWithOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(resetPasswordWithOtp.fulfilled, (state, action) => { state.loading = false; state.verificationMessage = action.payload; state.error = null; })
      .addCase(resetPasswordWithOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const {
  clearError,
  clearVerificationMessage,
  clearRegistrationSuccess,
  updateUser,
  setLoading,
} = authSlice.actions;

export default authSlice.reducer;